import { BrowserRouter, Routes, Route, useLocation, Link, Navigate } from 'react-router-dom';
import { captureAttribution } from './lib/attribution';
import { captureFbc } from './lib/pixel';
captureAttribution();
captureFbc();
import { CartProvider } from './contexts/CartContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { CustomerAuthProvider, useCustomerAuth } from './contexts/CustomerAuthContext';
import { Toaster, toast } from 'sonner';
import { FloatingCart } from './components/FloatingCart';
import { DiwaliPopup } from './components/DiwaliPopup';
import { CustomerAvatar } from './components/CustomerAvatar';
import { RequireAdmin } from './components/admin/RequireAdmin';
import { AdminErrorBoundary } from './components/admin/AdminErrorBoundary';
const AdminLayout = lazy(() => import('./components/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })));
const LoginSheet = lazy(() => import('./components/LoginSheet').then((m) => ({ default: m.LoginSheet })));
import Home from './pages/Home';
import { trackPageview } from './lib/analytics';
import { fbTrack } from './lib/pixel';
import ProductDetail from './pages/ProductDetail';
import { lazy, Suspense } from 'react';

// Admin pages are code-split so the storefront bundle stays small (recharts &
// all admin code load only when an /admin route is visited) and each admin
// link fetches a tiny cached chunk — much faster first paint.
const AdminForgotPassword = lazy(() => import('./pages/admin/AdminForgotPassword'));
const AdminResetPassword = lazy(() => import('./pages/admin/AdminResetPassword'));
const AdminChangePassword = lazy(() => import('./pages/admin/AdminChangePassword'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminHome = lazy(() => import('./components/admin/AdminHome'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminShipments = lazy(() => import('./pages/admin/AdminShipments'));
const AdminOrderDetail = lazy(() => import('./pages/admin/AdminOrderDetail'));
const MasterSearch = lazy(() => import('./pages/admin/MasterSearch'));
const AdminCustomers = lazy(() => import('./pages/admin/AdminCustomers'));
const AdminCustomerDetail = lazy(() => import('./pages/admin/AdminCustomerDetail'));
const AdminAccounts = lazy(() => import('./pages/admin/AdminAccounts'));
const AdminInventory = lazy(() => import('./pages/admin/AdminInventory'));
const AdminPricing = lazy(() => import('./pages/admin/AdminPricing'));
const AdminInbox = lazy(() => import('./pages/admin/AdminInbox'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminCampaigns = lazy(() => import('./pages/admin/AdminCampaigns'));
const AdminAds = lazy(() => import('./pages/admin/AdminAds'));
const People = lazy(() => import('./pages/admin/People'));
const AdminLiveChat = lazy(() => import('./pages/admin/AdminLiveChat'));
const AdminCoupons = lazy(() => import('./pages/admin/AdminCoupons'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const AdminPress = lazy(() => import('./pages/admin/AdminPress'));
const AdminGallery = lazy(() => import('./pages/admin/AdminGallery'));
const AdminSocial = lazy(() => import('./pages/admin/AdminSocial'));
const AdminAmbassadors = lazy(() => import('./pages/admin/AdminAmbassadors'));
const Ambassador = lazy(() => import('./pages/Ambassador'));
const AmbassadorApply = lazy(() => import('./pages/AmbassadorApply'));
const BestPaddleIndia = lazy(() => import('./pages/BestPaddleIndia'));
const PickleballIndia = lazy(() => import('./pages/PickleballIndia'));
const Press = lazy(() => import('./pages/Press'));
const NotifyPage = lazy(() => import('./pages/NotifyPage'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const Checkout = lazy(() => import('./pages/Checkout'));
const OrderConfirmation = lazy(() => import('./pages/OrderConfirmation'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
import { NewsletterSignup } from './components/NewsletterSignup';
import { CookieConsent, openCookiePreferences } from './components/CookieConsent';
import { FOOTER_LEGAL_LINKS, COMPANY } from './data/legalContent';
import { AmazonTextLink } from './components/AmazonBuy';
import Technicals from './pages/Technicals';
import Compare from './pages/Compare';
import { Menu, X, User, LogOut, ShoppingBag } from 'lucide-react';
import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import './App.css';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginMounted, setLoginMounted] = useState(false);
  const [loginMode, setLoginMode] = useState('login');
  const { customer } = useCustomerAuth();
  useEffect(() => { if (showLogin) setLoginMounted(true); }, [showLogin]);

  return (
    <header className="sticky top-0 z-40 bg-[#0B0B0C]/95 backdrop-blur-sm border-b border-zinc-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <a href="/" className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_d8754516-b3fb-443a-ac0e-88ac7ea31a39/artifacts/aealbsng_111.png"
              alt="DYSSC"
              className="h-10 w-auto"
            />
          </a>

          <nav className="hidden md:flex items-center gap-8">
            <a href="/#shop" className="text-sm text-zinc-300 hover:text-white transition-colors">
              SHOP
            </a>
            <Link to="/technicals" className="text-sm text-zinc-300 hover:text-white transition-colors" data-testid="nav-technicals">
              TECHNICALS
            </Link>
            <Link to="/compare" className="text-sm text-zinc-300 hover:text-white transition-colors" data-testid="nav-compare">
              COMPARE
            </Link>
            <Link to="/blog" className="text-sm text-zinc-300 hover:text-white transition-colors" data-testid="nav-blog">
              BLOGS
            </Link>
            <Link to="/erne" className="inline-flex items-center gap-2 text-sm font-semibold hover:text-white transition-colors" style={{ color: '#B5FF00' }} data-testid="nav-ambassador">
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/erne/erne_medal_sm.webp`} alt="" className="h-6 w-auto -my-1" draggable={false} data-testid="nav-erne-medal" />
              THE ERNE
            </Link>
            {customer ? (
              <Link to="/account" className="text-sm text-zinc-300 hover:text-white transition-colors" data-testid="nav-my-account">
                MY ACCOUNT
              </Link>
            ) : (
              <a href="/#contact" className="text-sm text-zinc-300 hover:text-white transition-colors">
                CONTACT
              </a>
            )}
            <UserMenu
              onLoginClick={() => { setLoginMode('login'); setShowLogin(true); }}
            />
          </nav>

          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            data-testid="navbar-mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <nav className="md:hidden py-4 space-y-3 border-t border-zinc-800">
            <a
              href="/#shop"
              className="block text-sm text-zinc-300 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              SHOP
            </a>
            <Link
              to="/technicals"
              className="block text-sm text-zinc-300 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-technicals-mobile"
            >
              TECHNICALS
            </Link>
            <Link
              to="/compare"
              className="block text-sm text-zinc-300 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-compare-mobile"
            >
              COMPARE
            </Link>
            <Link
              to="/blog"
              className="block text-sm text-zinc-300 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-blog-mobile"
            >
              BLOGS
            </Link>
            <Link
              to="/erne"
              className="flex items-center gap-2 text-sm font-semibold hover:text-white transition-colors"
              style={{ color: '#B5FF00' }}
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-ambassador-mobile"
            >
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/erne/erne_medal_sm.webp`} alt="" className="h-6 w-auto" draggable={false} />
              THE ERNE · U21 FELLOWSHIP
            </Link>
            <a
              href="/#contact"
              className="block text-sm text-zinc-300 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="nav-contact-mobile"
            >
              CONTACT
            </a>
            <div className="pt-2 border-t border-zinc-800">
              <UserMenu
                mobile
                onLoginClick={() => { setMobileMenuOpen(false); setLoginMode('login'); setShowLogin(true); }}
              />
            </div>
          </nav>
        )}
      </div>

      {loginMounted && <Suspense fallback={null}><LoginSheet open={showLogin} onClose={() => setShowLogin(false)} initialMode={loginMode} /></Suspense>}
    </header>
  );
};


function UserMenu({ onLoginClick, mobile = false }) {
  const { customer, checking, logout } = useCustomerAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (checking) return null;

  if (!customer) {
    if (mobile) {
      return (
        <button onClick={onLoginClick}
                className="text-sm text-zinc-300 hover:text-white w-full text-left"
                data-testid="navbar-login-btn">
          LOGIN / SIGN UP
        </button>
      );
    }
    return (
      <button
        onClick={onLoginClick}
        className="text-sm bg-white text-black hover:bg-zinc-200 rounded-full px-4 py-1.5 font-medium transition-colors"
        data-testid="navbar-login-btn"
      >
        Login / Sign Up
      </button>
    );
  }

  if (mobile) {
    return (
      <div className="space-y-2">
        <Link to="/account"
              className="flex items-center gap-2 text-sm text-zinc-300 hover:text-white"
              data-testid="navbar-account-link">
          <CustomerAvatar customer={customer} size="w-7 h-7" textSize="text-xs" testId="navbar-avatar-mobile" />
          MY ACCOUNT
        </Link>
        <Link to="/erne?enter=1"
              className="flex items-center gap-2 text-sm font-semibold hover:text-white"
              style={{ color: '#B5FF00' }}
              data-testid="navbar-erne-hub-link-mobile">
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/erne/erne_medal_sm.webp`} alt="" className="h-6 w-auto" draggable={false} />
          THE ERNE HUB
        </Link>
        <button onClick={() => { logout(); toast.success('Signed out'); }}
                className="text-sm text-zinc-500 hover:text-white"
                data-testid="navbar-logout-btn-mobile">
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="rounded-full hover:ring-2 hover:ring-[#E02128]/40 transition-shadow"
        data-testid="navbar-user-menu-btn"
      >
        <CustomerAvatar customer={customer} testId="navbar-avatar" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-[#111214] border border-zinc-800 rounded-xl py-2 shadow-2xl"
             data-testid="navbar-user-menu">
          <div className="px-3 py-2 border-b border-zinc-900 flex items-center gap-2.5">
            <CustomerAvatar customer={customer} testId="navbar-menu-avatar" />
            <div className="min-w-0">
              <div className="text-white text-sm font-medium truncate">{customer.name || 'Welcome'}</div>
              <div className="text-zinc-500 text-xs truncate">{customer.email}</div>
              <div className="text-zinc-600 text-[11px] font-mono mt-0.5">{customer.phone_display}</div>
            </div>
          </div>
          <Link to="/account" onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-900 hover:text-white"
                data-testid="navbar-account-link">
            <ShoppingBag className="w-3.5 h-3.5" />
            My orders
          </Link>
          <Link to="/erne?enter=1" onClick={() => setOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold hover:bg-zinc-900 hover:text-white"
                style={{ color: '#B5FF00' }}
                data-testid="navbar-erne-hub-link">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/static/erne/erne_medal_sm.webp`} alt="" className="h-5 w-auto -ml-0.5" draggable={false} />
            The Erne hub
          </Link>
          <button
            onClick={() => { setOpen(false); logout(); toast.success('Signed out'); }}
            className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-900 hover:text-white"
            data-testid="navbar-logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const Footer = () => {
  return (
    <footer className="bg-[#0B0B0C] border-t border-zinc-800 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 pb-10 border-b border-zinc-800 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="font-heading font-semibold text-white text-lg">Stay in the game</h3>
            <p className="text-zinc-400 text-sm mt-1">Drops, restocks & members-only deals — straight to your inbox.</p>
          </div>
          <NewsletterSignup source="footer" variant="inline" />
        </div>
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_d8754516-b3fb-443a-ac0e-88ac7ea31a39/artifacts/aealbsng_111.png"
                alt="DYSSC"
                className="h-10 w-auto"
              />
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_paddle-shop/artifacts/gk9axm6v_SMASH.%20SWEAT.%20REPEAT..png"
              alt="SMASH. SWEAT. REPEAT."
              className="h-8 md:h-10 w-auto object-contain mb-4"
              data-testid="footer-smash-sweat-repeat"
            />
            <p className="text-zinc-400 text-sm">
              Engineered with Purpose. Crafted to Perform.
            </p>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-white mb-4">Quick Links</h3>
            <div className="space-y-2">
              <a href="/" className="block text-sm text-zinc-400 hover:text-white transition-colors">
                Home
              </a>
              <a href="/#shop" className="block text-sm text-zinc-400 hover:text-white transition-colors">
                Shop
              </a>
              <a href="/best-pickleball-paddle-india" className="block text-sm text-zinc-400 hover:text-white transition-colors">
                Best Paddle in India
              </a>
              <a href="/pickleball-india" className="block text-sm text-zinc-400 hover:text-white transition-colors">
                Pickleball in India
              </a>
              <a href="/#technology" className="block text-sm text-zinc-400 hover:text-white transition-colors">
                Technology
              </a>
              <Link to="/technicals" className="block text-sm text-zinc-400 hover:text-white transition-colors" data-testid="footer-technicals">
                Technicals
              </Link>
              <Link to="/compare" className="block text-sm text-zinc-400 hover:text-white transition-colors" data-testid="footer-compare">
                Compare Paddles
              </Link>
              <Link to="/blog" className="block text-sm text-zinc-400 hover:text-white transition-colors" data-testid="footer-blog">
                Blogs
              </Link>
              <Link to="/press" className="block text-sm text-zinc-400 hover:text-white transition-colors" data-testid="footer-press">
                Press
              </Link>
              <Link to="/erne" className="block text-sm text-zinc-400 hover:text-white transition-colors" data-testid="footer-ambassador">
                The Erne (U21 Fellowship)
              </Link>
              <a href="/#contact" className="block text-sm text-zinc-400 hover:text-white transition-colors">
                Contact
              </a>
            </div>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-white mb-4">Legal</h3>
            <div className="space-y-2">
              {FOOTER_LEGAL_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="block text-sm text-zinc-400 hover:text-white transition-colors"
                  data-testid={`footer-legal-${l.to.replace('/', '')}`}
                >
                  {l.label}
                </Link>
              ))}
              <button
                type="button"
                onClick={openCookiePreferences}
                className="block text-sm text-zinc-400 hover:text-white transition-colors text-left"
                data-testid="footer-cookie-preferences"
              >
                Cookie Preferences
              </button>
            </div>
          </div>

          <div>
            <h3 className="font-heading font-semibold text-white mb-4">Contact</h3>
            <div className="space-y-2 text-sm text-zinc-400">
              <p className="text-white font-semibold">{COMPANY.legalName}</p>
              <a href={`mailto:${COMPANY.email}`} className="block hover:text-white transition-colors">{COMPANY.email}</a>
              <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="block hover:text-white transition-colors">{COMPANY.phone}</a>
              <p>Mon-Fri: 9AM - 6PM IST</p>
              <AmazonTextLink url={COMPANY.amazon.hexcore} placement="footer" className="pt-1" />
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-zinc-800 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-zinc-500">
          <p>&copy; {new Date().getFullYear()} DYSSC by GameCore LLP. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1">
            {FOOTER_LEGAL_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="hover:text-zinc-300 transition-colors">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

function StoreLayout({ children }) {
  return (
    <>
      <Header />
      {children}
      <Footer />
      <FloatingCart />
      <DiwaliPopup />
      {/* Floating chat widget hidden per user request (Aug 2026) — restore by importing and re-adding <LiveChatWidget /> from ./components/LiveChatWidget */}
      <CookieConsent />
    </>
  );
}

function PageFallback() {
  return <div className="min-h-[60vh]" data-testid="page-loading" />;
}

function AdminPageFallback() {
  return (
    <div className="flex items-center justify-center py-24" data-testid="admin-page-loading">
      <div className="w-6 h-6 border-2 border-zinc-300 border-t-[#E02128] rounded-full animate-spin" />
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  const isAdmin = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  return isAdmin ? (
    <AdminErrorBoundary>
    <Suspense fallback={<AdminPageFallback />}>
    <Routes>
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
      <Route path="/admin/reset-password" element={<AdminResetPassword />} />
      <Route path="/admin/change-password" element={<RequireAdmin><AdminChangePassword /></RequireAdmin>} />
      <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
        <Route index element={<AdminHome />} />
        <Route path="dashboard" element={<RequireAdmin permission="dashboard"><AdminDashboard /></RequireAdmin>} />
        <Route path="search" element={<RequireAdmin permission="orders"><MasterSearch /></RequireAdmin>} />
        <Route path="orders" element={<RequireAdmin permission="orders"><AdminOrders /></RequireAdmin>} />
        <Route path="shipments" element={<RequireAdmin permission="orders"><AdminShipments /></RequireAdmin>} />
        <Route path="orders/:id" element={<RequireAdmin permission="orders"><AdminOrderDetail /></RequireAdmin>} />
        <Route path="customers" element={<RequireAdmin permission="customers"><AdminCustomers /></RequireAdmin>} />
        <Route path="customers/:email" element={<RequireAdmin permission="customers"><AdminCustomerDetail /></RequireAdmin>} />
        <Route path="people" element={<RequireAdmin permission="people"><People /></RequireAdmin>} />
        <Route path="inventory" element={<RequireAdmin permission="inventory"><AdminInventory /></RequireAdmin>} />
        <Route path="pricing" element={<RequireAdmin permission="pricing"><AdminPricing /></RequireAdmin>} />
        <Route path="inbox" element={<RequireAdmin permission="inbox"><AdminInbox /></RequireAdmin>} />
        <Route path="inbox/:wa_id" element={<RequireAdmin permission="inbox"><AdminInbox /></RequireAdmin>} />
        <Route path="live-chats" element={<RequireAdmin permission="livechat"><AdminLiveChat /></RequireAdmin>} />
        <Route path="coupons" element={<RequireAdmin permission="coupons"><AdminCoupons /></RequireAdmin>} />
        <Route path="reviews" element={<RequireAdmin permission="reviews"><AdminReviews /></RequireAdmin>} />
        <Route path="press" element={<RequireAdmin permission="blog"><AdminPress /></RequireAdmin>} />
        <Route path="gallery" element={<RequireAdmin permission="blog"><AdminGallery /></RequireAdmin>} />
        <Route path="accounts" element={<RequireAdmin permission="accounts"><AdminAccounts /></RequireAdmin>} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="campaigns" element={<RequireAdmin permission="campaigns"><AdminCampaigns /></RequireAdmin>} />
        <Route path="ads" element={<RequireAdmin permission="ads"><AdminAds /></RequireAdmin>} />
        <Route path="social" element={<RequireAdmin permission="blog"><AdminSocial /></RequireAdmin>} />
        <Route path="ambassadors" element={<RequireAdmin permission="ambassadors"><AdminAmbassadors /></RequireAdmin>} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
    </Routes>
    </Suspense>
    </AdminErrorBoundary>
  ) : (
    <StoreRoutes />
  );
}

function StoreRoutes() {
  const location = useLocation();

  // On every route change, jump the new page to the top.
  // Without this, navigating from a scrolled-down homepage to a product page
  // would open it at the previous scroll offset (mid-page).
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const firstView = useRef(true);
  useEffect(() => {
    trackPageview(location.pathname);
    // The static Pixel base code already fired the first PageView.
    if (firstView.current) { firstView.current = false; return; }
    fbTrack('PageView');
  }, [location.pathname]);

  return (
    <StoreLayout>
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/notify/:slug" element={<NotifyPage />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/review/:token" element={<ReviewPage />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/privacy" element={<LegalPage docKey="privacy" />} />
        <Route path="/best-pickleball-paddle-india" element={<BestPaddleIndia />} />
        <Route path="/pickleball-india" element={<PickleballIndia />} />
        <Route path="/press" element={<Press />} />
        <Route path="/technicals" element={<Technicals />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/erne" element={<Ambassador />} />
        <Route path="/erne/apply" element={<AmbassadorApply />} />
        <Route path="/ambassador" element={<Navigate to="/erne" replace />} />
        <Route path="/ambassador/apply" element={<Navigate to="/erne/apply" replace />} />
        <Route path="/consumer-data-policy" element={<LegalPage docKey="consumer-data-policy" />} />
        <Route path="/terms" element={<LegalPage docKey="terms" />} />
        <Route path="/shipping-returns" element={<LegalPage docKey="shipping-returns" />} />
        <Route path="/data-deletion" element={<LegalPage docKey="data-deletion" />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </StoreLayout>
  );
}

function NotFound() {
  return (
    <section className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6 pt-28 pb-16" data-testid="not-found-page">
      <p className="text-[#E02128] font-mono text-sm tracking-[0.3em] mb-4">ERROR 404</p>
      <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Out of bounds.</h1>
      <p className="text-zinc-400 mb-8 max-w-md">The page you're looking for doesn't exist or has moved.</p>
      <Link to="/" className="px-6 py-3 rounded-full bg-[#E02128] text-white font-semibold hover:bg-red-600 transition-colors" data-testid="not-found-home-link">Back to home</Link>
    </section>
  );
}

function App() {
  return (
    <CartProvider>
      <CustomerAuthProvider>
        <AdminAuthProvider>
          <BrowserRouter>
            <div className="App min-h-screen bg-[#0B0B0C]">
              <AppRoutes />
              <Toaster
                theme="dark"
                position="top-center"
                offset={16}
                toastOptions={{
                  duration: 3000,
                  unstyled: true,
                  className: 'dyssc-toast',
                }}
              />
            </div>
          </BrowserRouter>
        </AdminAuthProvider>
      </CustomerAuthProvider>
    </CartProvider>
  );
}

export default App;
